/**
 * Normas agregadas para MINI_IPIP_20.
 * Jerarquía: company → industry → country → global → global_seed → fallback.
 */

import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export const MINI_IPIP_NORM_VERSION = 'mini_ipip_20_v1';
const MIN_N_VALID = 30;
const MIN_SD = 0.05;

export type NormScopeType = 'company' | 'industry' | 'country' | 'global' | 'global_seed' | 'fallback';

export const MINI_IPIP_DIM_KEYS = [
  'responsabilidadOrden',
  'estabilidadEmocional',
  'energiaSocial',
  'cooperacion',
  'aperturaAdaptabilidad',
] as const;

export type MiniIpipDimKey = (typeof MINI_IPIP_DIM_KEYS)[number];

export interface MiniIpipDimNorm {
  sum: number;
  sumSq: number;
  meanAvg: number;
  sdAvg: number;
}

export interface MiniIpipNormDoc {
  normVersion: string;
  scopeType: 'global' | 'global_seed' | 'industry' | 'company' | 'country';
  scopeKey: string;
  n: number;
  updatedAt: ReturnType<typeof serverTimestamp>;
  dims: Record<MiniIpipDimKey, MiniIpipDimNorm>;
}

export interface MiniIpipNormResult {
  normUsed: NormScopeType;
  nNorm: number;
  dims: Record<MiniIpipDimKey, { meanAvg: number; sdAvg: number }>;
}

function scopeId(scopeType: string, scopeKey: string): string {
  if (scopeType === 'global' || scopeType === 'global_seed') return scopeType;
  if (scopeType === 'country') return `country:${scopeKey}`;
  return `${scopeType}:${scopeKey}`;
}

function emptyDim(): MiniIpipDimNorm {
  return { sum: 0, sumSq: 0, meanAvg: 0, sdAvg: 0 };
}

function emptyDims(): Record<MiniIpipDimKey, MiniIpipDimNorm> {
  return {
    responsabilidadOrden: emptyDim(),
    estabilidadEmocional: emptyDim(),
    energiaSocial: emptyDim(),
    cooperacion: emptyDim(),
    aperturaAdaptabilidad: emptyDim(),
  };
}

function computeSd(sum: number, sumSq: number, n: number): number {
  if (n <= 0) return 0;
  const mean = sum / n;
  const variance = Math.max(sumSq / n - mean * mean, 1e-6);
  return Math.sqrt(variance);
}

function isNormValid(n: number, dims: Record<string, MiniIpipDimNorm>): boolean {
  if (n < MIN_N_VALID) return false;
  for (const d of Object.values(dims)) {
    if (d.sdAvg < MIN_SD) return false;
  }
  return true;
}

/**
 * Obtiene la mejor norma disponible en la jerarquía.
 * company → industry → country → global → fallback
 */
export async function getMiniIpipNorm(params: {
  companyId: string;
  industryKey?: string | null;
  countryCode?: string | null;
  firestore?: Firestore;
}): Promise<MiniIpipNormResult | null> {
  const firestore = params.firestore ?? db;
  const order: { scopeType: 'company' | 'industry' | 'country' | 'global'; scopeKey: string }[] = [
    { scopeType: 'company', scopeKey: params.companyId },
    { scopeType: 'industry', scopeKey: params.industryKey ?? '' },
    { scopeType: 'country', scopeKey: params.countryCode ?? '' },
    { scopeType: 'global', scopeKey: 'global' },
  ];

  for (const { scopeType, scopeKey } of order) {
    if (scopeType === 'industry' && !scopeKey) continue;
    if (scopeType === 'country' && !scopeKey) continue;
    const id = scopeId(scopeType, scopeKey);
    const ref = doc(firestore, 'norms', 'mini_ipip_20', 'scopes', id);
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const data = snap.data() as MiniIpipNormDoc;
    if (data.normVersion !== MINI_IPIP_NORM_VERSION) continue;
    if (!isNormValid(data.n, data.dims)) continue;

    const dims: Record<MiniIpipDimKey, { meanAvg: number; sdAvg: number }> = {} as Record<
      MiniIpipDimKey,
      { meanAvg: number; sdAvg: number }
    >;
    for (const k of MINI_IPIP_DIM_KEYS) {
      const d = data.dims[k];
      if (d) dims[k] = { meanAvg: d.meanAvg, sdAvg: d.sdAvg };
    }
    return {
      normUsed: scopeType,
      nNorm: data.n,
      dims,
    };
  }
  return null;
}

/**
 * Actualiza las normas con un nuevo resultado.
 * excludeFromNorms=true → no actualiza nada
 * excludeFromNorms=false → company, industry, country (si aplica), global
 */
export async function updateMiniIpipNorms(params: {
  companyId: string;
  industryKey?: string | null;
  countryCode?: string | null;
  excludeFromNorms: boolean;
  dimAvgs: Record<MiniIpipDimKey, number>;
  firestore?: Firestore;
}): Promise<void> {
  const firestore = params.firestore ?? db;
  if (params.excludeFromNorms) return;

  const dimAvgs = params.dimAvgs;

  const scopes: { scopeType: 'company' | 'industry' | 'country' | 'global'; scopeKey: string }[] = [
    { scopeType: 'company', scopeKey: params.companyId },
    { scopeType: 'industry', scopeKey: params.industryKey ?? '' },
    { scopeType: 'country', scopeKey: params.countryCode ?? '' },
    { scopeType: 'global', scopeKey: 'global' },
  ].filter((s) => {
    if (s.scopeType === 'industry') return !!s.scopeKey;
    if (s.scopeType === 'country') return !!s.scopeKey;
    return true;
  });

  for (const { scopeType, scopeKey } of scopes) {
    const id = scopeId(scopeType, scopeKey);
    const ref = doc(firestore, 'norms', 'mini_ipip_20', 'scopes', id);

    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(ref);
      let n = 0;
      let dims: Record<MiniIpipDimKey, MiniIpipDimNorm> = emptyDims();

      if (snap.exists()) {
        const data = snap.data() as MiniIpipNormDoc;
        if (data.normVersion !== MINI_IPIP_NORM_VERSION) return;
        n = data.n;
        dims = { ...data.dims };
      }

      n += 1;
      for (const k of MINI_IPIP_DIM_KEYS) {
        const avg = dimAvgs[k] ?? 0;
        dims[k] = {
          sum: dims[k].sum + avg,
          sumSq: dims[k].sumSq + avg * avg,
          meanAvg: 0,
          sdAvg: 0,
        };
        dims[k].meanAvg = dims[k].sum / n;
        dims[k].sdAvg = computeSd(dims[k].sum, dims[k].sumSq, n);
      }

      tx.set(ref, {
        normVersion: MINI_IPIP_NORM_VERSION,
        scopeType,
        scopeKey,
        n,
        updatedAt: serverTimestamp(),
        dims,
      });
    });
  }
}
